import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TableComponent } from '../../../../shared/component/table/table.component';
import { AgGridTableService } from '../../../../shared/services/ag-grid.service';
import { ReportPaginationService, ReportPayload } from '../../Reports/report-pagination.service';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-evc-daily-consumption',
  standalone: true,
  imports: [CommonModule, TableComponent],
  providers: [AgGridTableService, ReportPaginationService],
  template: `
    <div class="evc-section">
      <div class="evc-sec-head">
        <div class="evc-sec-title">
          <span class="evc-sec-accent"></span>
          <div>
            <span class="evc-sec-name">EV Daily Consumption Report&nbsp;</span>
            <span class="evc-sec-sub">Day-by-day Grid & DG consumed energy units (kWh)</span>
          </div>
        </div>
        <div class="view-toggle">
          <button class="vt-btn" [class.vt-active]="viewMode === 'grid'" (click)="setView('grid')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> Grid
          </button>
          <button class="vt-btn" [class.vt-active]="viewMode === 'chart'" (click)="setView('chart')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> Chart
          </button>
        </div>
      </div>
      <div class="evc-content-area">
        <div class="evc-grid-wrap" [class.view-hidden]="viewMode !== 'grid'">
           <app-table 
             [pageNation]="false" 
             [DateFilterShow]="true" 
             [paginationPageSizeSelector]="[10, 20, 50]" 
             [maxRowCount]="totalCount" 
             (onPageChange)="handlePageChange($event)" 
             [ExportFileName]="exportFileName"
             (filtersApplied)="onFiltersApplied($event)">
           </app-table>
        </div>
        <div class="evc-chart-wrap" [class.view-hidden]="viewMode !== 'chart'">
          <canvas #chartCanvas class="evc-canvas"></canvas>
        </div>
      </div>
    </div>
  `,
  styleUrl: '../ev-charger.component.scss'
})
export class EvcDailyConsumptionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() locationId: string = '';
  @Input() siteId: string = '';
  @Input() evName?: string;

  @ViewChild('chartCanvas', { static: false }) chartCanvasRef?: ElementRef<HTMLCanvasElement>;

  viewMode: 'grid' | 'chart' = 'grid';
  chartInstance?: Chart;
  private rowData: any[] = [];
  fromDate: string = '';
  toDate: string = '';
  totalCount: number = 0;

  get exportFileName(): string {
    const namePart = this.evName ? ` ${this.evName}` : '';
    return `EV Daily Consumption Report${namePart} ${new Date().toISOString().slice(0, 10)}`;
  }

  private destroy$ = new Subject<void>();
  private isDestroyed = false;
  private renderTimer: any = null;

  constructor(
    private tableService: AgGridTableService,
    public reportPaginationService: ReportPaginationService,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    if (!this.locationId) {
      this.locationId = localStorage.getItem('login_id') || localStorage.getItem('location_id') || '9440991006';
    }
    if (!this.siteId) {
      this.siteId = sessionStorage.getItem('consumerSiteId') || localStorage.getItem('selected_site_id') || localStorage.getItem('site_idLogin') || localStorage.getItem('site_id') || '59.64062087';
    }

    const dTo = new Date();
    const dFrom = new Date();
    dFrom.setDate(dTo.getDate() - 1);
    this.fromDate = dFrom.toISOString().split('T')[0];
    this.toDate = dTo.toISOString().split('T')[0];

    this.reportPaginationService.totalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.totalCount = count;
      });

    this.tableService.tableData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (this.isDestroyed) return;
        this.rowData = (state?.rowData || []).filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
        if (this.viewMode === 'chart' && this.rowData.length > 0) {
          if (this.renderTimer) clearTimeout(this.renderTimer);
          this.renderTimer = setTimeout(() => this.renderChart(), 80);
        }
      });

    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    const locChange = changes['locationId'];
    const siteChange = changes['siteId'];
    if ((locChange && !locChange.firstChange && locChange.currentValue) ||
      (siteChange && !siteChange.firstChange && siteChange.currentValue)) {
      this.loadData();
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.renderTimer) {
      clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }
  }

  setView(mode: 'grid' | 'chart') {
    this.viewMode = mode;
    if (mode === 'chart') {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => this.renderChart(), 80);
    }
  }

  onFiltersApplied(event: { from_date: string; to_date: string; site_id?: string }) {
    if (event.from_date && event.to_date) {
      this.fromDate = event.from_date;
      this.toDate = event.to_date;
      if (event.site_id) {
        this.siteId = event.site_id;
      }
      this.loadData();
    }
  }

  handlePageChange(event: { page: number; pageSize: number }) {
    this.reportPaginationService.changePage(event, this.destroy$);
  }

  loadData() {
    const servicePayload: ReportPayload = {
      from_date: this.fromDate,
      to_date: this.toDate,
      site_ids: this.siteId,
      loc_id: this.locationId,
      is_paginate: 'N',
    };

    this.reportPaginationService.initFilters(
      servicePayload,
      'DAILY_REPORT',
      this.destroy$,
      '',
      'Reports',
      null,
      false
    );
  }

  renderChart() {
    if (this.isDestroyed) return;
    const canvas = this.chartCanvasRef?.nativeElement;
    if (!canvas || !canvas.isConnected) return;
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }

    const nonDummyRows = this.rowData.filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
    if (nonDummyRows.length === 0) return;
    const labels = nonDummyRows.map(r => r['date'] || r['Date'] || r['reading_date'] || r['day'] || 'Day');
    const gridConsumedData = nonDummyRows.map(r => parseFloat(r['unit_grid_consumed'] ?? r['unit_grid_closing'] ?? r['energy_kwh'] ?? r['grid_unit'] ?? 0));
    const dgConsumedData = nonDummyRows.map(r => parseFloat(r['unit_dg_consumed'] ?? r['unit_dg_closing'] ?? r['dg_unit'] ?? 0));

    this.ngZone.runOutsideAngular(() => {
      if (this.isDestroyed || !canvas.isConnected) return;
      this.chartInstance = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Grid Unit Consumed',
              data: gridConsumedData,
              fill: true,
              backgroundColor: 'rgba(34, 197, 94, 0.25)',
              borderColor: '#22c55e',
              borderWidth: 2,
              tension: 0.35,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#22c55e',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            },
            {
              label: 'DG Unit Consumed',
              data: dgConsumedData,
              fill: true,
              backgroundColor: 'rgba(245, 158, 11, 0.25)',
              borderColor: '#f59e0b',
              borderWidth: 2,
              tension: 0.35,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#f59e0b',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                boxWidth: 14,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || '';
                  const value = context.parsed.y ?? 0;
                  return ` ${label}: ${value.toLocaleString()} kWh`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { color: 'rgba(200, 200, 200, 0.15)' },
              ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 15 }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(200, 200, 200, 0.15)' },
              title: { display: true, text: 'Consumed Units (kWh)' }
            }
          }
        }
      });
    });
  }
}
